import {
  AMBOSS_DOMAIN,
  AMBOSS_EMBED_HOST,
  MICROSOFT_AUTH_DOMAINS,
  matchesDomain,
} from './ambossHosts';

export type AmbossMode = 'ai' | 'search';

const SEARCH_BASE = `https://${AMBOSS_EMBED_HOST}/de/search?embed=1&q=`;
const AI_MODE_BASE = `https://${AMBOSS_EMBED_HOST}/de/ai-mode?embed=1&q=`;
const AI_MODE_CID_BASE = `https://${AMBOSS_EMBED_HOST}/de/ai-mode?embed=1&cid=`;

const SSO_LOGIN_URL = 'https://www.amboss.com/de/auth-sso/login/Ov0IZR';

// AMBOSS currently drops `nextLink`; it is still the intended usage of the
// link and tracks the target URL should they start honoring it.
export function wrapWithSso(targetUrl: string): string {
  return `${SSO_LOGIN_URL}?nextLink=${encodeURIComponent(targetUrl)}`;
}

export function isAuthNavigation(rawUrl: string): boolean {
  try {
    const { hostname, pathname } = new URL(rawUrl);
    if (MICROSOFT_AUTH_DOMAINS.some((domain) => matchesDomain(hostname, domain))) {
      return true;
    }
    if (matchesDomain(hostname, AMBOSS_DOMAIN)) {
      return /(^|\/)(auth-sso|login|account\/login)(\/|$)/.test(pathname);
    }
    return false;
  } catch {
    return false;
  }
}

export function isEmbedHostLanding(rawUrl: string): boolean {
  try {
    return new URL(rawUrl).hostname === AMBOSS_EMBED_HOST;
  } catch {
    return false;
  }
}

export type AmbossEmbedMechanism = 'iframe' | 'webview';

// AMBOSS's pages set `frame-ancestors`, which blocks framing the app in an
// <iframe> from the packaged Electron origin (app://). Electron therefore
// embeds via <webview> (a top-level document, exempt from frame-ancestors);
// the browser build uses an <iframe> against an allowlisted web origin.
export function getEmbedMechanism(): AmbossEmbedMechanism {
  const isElectron = Boolean((window as { electronAPI?: unknown }).electronAPI);
  return isElectron ? 'webview' : 'iframe';
}

// A cid resumes an existing ai-mode conversation (`?cid=`); otherwise the query
// opens a fresh one (`?q=`). Search mode has no conversation, so it ignores cid.
export function buildSearchUrl(query: string, mode: AmbossMode, cid?: string): string {
  if (mode === 'ai' && cid) return `${AI_MODE_CID_BASE}${encodeURIComponent(cid)}`;
  const base = mode === 'ai' ? AI_MODE_BASE : SEARCH_BASE;
  return `${base}${encodeURIComponent(query)}`;
}

export interface AmbossHelpLink {
  labelKey: string;
  url: string;
}

export const AMBOSS_HELP_LINKS: AmbossHelpLink[] = [
  { labelKey: 'amboss.help.links.contact', url: 'https://support.amboss.com/hc/de' },
  {
    labelKey: 'amboss.help.links.terms',
    url: 'https://www.amboss.com/de/de-legal/nutzungsbedingungen',
  },
  { labelKey: 'amboss.help.links.liability', url: 'https://www.amboss.com/de/de-legal/disclaimer' },
  { labelKey: 'amboss.help.links.privacy', url: 'https://www.amboss.com/de/de-legal/datenschutz' },
  { labelKey: 'amboss.help.links.imprint', url: 'https://www.amboss.com/de/de-legal/impressum' },
];
